import { Descendant } from 'slate';
import {
	faTimes,
	faCheck,
	faPlus,
	faVideo,
	faPassport,
	faFileContract,
	faFileSignature,
	faPause,
	faPen,
	faMoneyBill,
	faStar,
	faBriefcase,
	faUsers,
	faHandshake,
	faCog,
	faCalendar,
	faEnvelope,
	faPhone,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import {
	Job,
	User,
	CompleteUser,
	Action,
	providerKeys,
	CandidateSource,
	WorkingPatternKeys,
	ContractTypeKeys,
	CompanyPlacement,
	InterviewFilters,
	NotificationFilters,
	PaginationFilters,
	AuditLogFilterTypes,
} from '../types';
import { serialize } from '../../components/WYSIWYG/utils/serialize';
import { ConfigurableIntegrations } from '../types/integrationTypes';

export const defaultTemplateOption = {
	label: 'Please select a template',
	value: '',
	disabled: true,
};

export const defaultEmptyTemplateOption = {
	label: 'Please select a template',
	value: '',
	disabled: true,
};

export const defaultWYSIWYGState = [
	{
		type: 'paragraph',
		children: [{ text: '' }],
	},
] as Descendant[];

export const defaultJobFilters = {
	filters: {
		status: 'LIVE',
		department: [],
	},
	limit: 10,
	order: 'asc',
	page: 1,
	sort: 'createdAt',
};

export const defaultFilters = {
	term: '',
	filters: {},
};

export const defaultPaginationFilters: PaginationFilters = {
	page: 1,
	limit: 10,
	sort: 'createdAt',
	order: 'desc',
};

export const initialTemplate = {
	companyOverview: defaultWYSIWYGState,
	jobDescription: defaultWYSIWYGState,
};

export const defaultUser: Omit<User, '_id'> = {
	enableEmailNotifications: true,
	email: '',
	emailAddresses: [],
	name: '',
	firstName: '',
	surname: '',
	permissions: [],
	roles: [],
	createdByControl: false,
	company: '',
	jobTitle: '',
	entities: [],
	recruiterModeEnabled: false,
	onboardingProgress: [],
	widgets: [],
};

export const defaultCompleteUser: Omit<CompleteUser, '_id'> = {
	...defaultUser,
	activeRoles: [],
	activePermissions: [],
};

export const remunerationPeriods = [
	{ label: 'Annually', value: 'YEAR' },
	{ label: 'Monthly', value: 'MONTH' },
	{ label: 'Weekly', value: 'WEEK' },
	{ label: 'Daily', value: 'DAY' },
	{ label: 'Hourly', value: 'HOUR' },
];

export const remunerationPeriodLabels = {
	YEAR: 'Annually',
	MONTH: 'Monthly',
	WEEK: 'Weekly',
	DAY: 'Daily',
	HOUR: 'Hourly',
};

export const defaultJob: Omit<Job, '_id'> = {
	reports: [],
	salaryFrom: 0,
	salaryTo: 0,
	title: '',
	remunerationPeriod: null,
	remoteWorkingAvailable: null,
	locationCountry: null,
	locationCity: null,
	notes: [],
	communicationEmail: '',
	department: null,
	contractType: null,
	workingPattern: null,
	internalNotes: '',
	openings: null,
	opensAt: null,
	closesAt: null,
	hoursPerWeek: null,
	financialNotes: defaultWYSIWYGState,
	currency: null,
	tags: [],
	description: {
		companyOverview: defaultWYSIWYGState,
		jobDescription: defaultWYSIWYGState,
	},
	flow: {
		buckets: [],
		template: '',
	},
	entity: '',
	shortRef: '',
	createdAt: '',
	updatedAt: '',
	applicationsCount: 0,
	acceptedApplicationsCount: 0,
	owner: {},
	internalUsers: [],
	externalUsers: [],
	status: 'DRAFT',
	promotion: {
		showOnCareersPage: false,
		allowExternalApplications: false,
		integrations: [],
	},
	applicationForm: {
		questions: [],
		links: [],
		files: [],
		cv: { enabled: true, required: true },
		coverLetter: { enabled: true, required: true },
	},
	openGraph: {
		title: '',
		description: '',
		image: '',
	},
};

export const contractTypes: { [key in ContractTypeKeys]: string } = {
	PERMANENT: 'Permanent',
	FIXED_TERM: 'Fixed term',
	SHORT_TERM: 'Short term',
	SEASONAL: 'Seasonal',
	CONTRACT: 'Contract',
	OTHER: 'Other',
};

export const workingPatterns: { [key in WorkingPatternKeys]: string } = {
	FULL_TIME: 'Full time',
	PART_TIME: 'Part time',
	TERM_TIME: 'Term time',
	SHIFTS: 'Shifts',
	OTHER: 'Other',
};

export const educationLevelsLabels = {
	NO_FORMAL_EDUCATION: 'No Formal Education',
	GCSE: 'GCSE',
	'A_LEVEL/BTEC': 'A Level / BTEC',
	BACHELORS_DEGREE: "Bachelor's Degree",
	POSTGRADUATE: 'Postgraduate',
	VOCATIONAL: 'Vocational',
	PREFER_NOT_TO_SAY: 'Prefer not to say',
	OTHER: 'Other',
};

export const educationLevels = [
	{
		label: 'No Formal Education',
		value: 'NO_FORMAL_EDUCATION',
	},
	{
		label: 'GCSE',
		value: 'GCSE',
	},
	{
		label: 'A Level / BTEC',
		value: 'A_LEVEL/BTEC',
	},
	{
		label: "Bachelor's Degree",
		value: 'BACHELORS_DEGREE',
	},
	{
		label: 'Postgraduate',
		value: 'POSTGRADUATE',
	},
	{
		label: 'Vocational',
		value: 'VOCATIONAL',
	},
	{
		label: 'Prefer not to say',
		value: 'PREFER_NOT_TO_SAY',
	},
	{
		label: 'Other',
		value: 'OTHER',
	},
];

export const defaultAction = {
	name: '',
	required: false,
	trigger: 'ENTRY',
	actionType: undefined,
	scheduledAfter: 0,
	data: {
		email: {
			body: serialize(defaultWYSIWYGState),
			to: [],
			subject: '',
			senderEmail: null,
		},
		interview: {
			message: serialize(defaultWYSIWYGState),
			internalParticipants: [],
			participants: [],
			useScheduler: true,
			attachments: [],
			startsAt: dayjs()
				.add(1, 'day')
				.add(1, 'hour')
				.set('minutes', 0)
				.set('seconds', 0)
				.toISOString(),
		},
		teams_message: {
			message: serialize(defaultWYSIWYGState),
			channel: '',
		},
		slack_message: {
			text: serialize(defaultWYSIWYGState),
			channel: '',
		},
	},
	triggerExternalOnly: false,
} as Partial<Action>;

export const defaultTagSuggestions = [
	'Tech',
	'Marketing',
	'Sales',
	'Project Management',
	'UI/UX',
];

export const addressTypes = [
	{
		value: 'HEAD_OFFICE',
		label: 'Head Office',
	},
	{
		value: 'REGIONAL_OFFICE',
		label: 'Regional Office',
	},
	{
		value: 'SATELLITE_OFFICE',
		label: 'Satellite Office',
	},
];

const addressTypeLabels = {
	HEAD_OFFICE: 'Head Office',
	REGIONAL_OFFICE: 'Regional Office',
	SATELLITE_OFFICE: 'Satellite Office',
};

export const countries = [
	{
		value: 'GB',
		label: 'United Kingdom',
	},
	{ value: 'AU', label: 'Australia' },
	{ value: 'CA', label: 'Canada' },
	{ value: 'NZ', label: 'New Zealand' },
	{ value: 'US', label: 'United States of America' },
	{ value: 'AF', label: 'Afghanistan' },
	{ value: 'AL', label: 'Albania' },
	{ value: 'DZ', label: 'Algeria' },
	{ value: 'AS', label: 'American Samoa' },
	{ value: 'AD', label: 'Andorra' },
	{ value: 'AO', label: 'Angola' },
	{ value: 'AI', label: 'Anguilla' },
	{ value: 'AQ', label: 'Antarctica' },
	{ value: 'AG', label: 'Antigua and Barbuda' },
	{ value: 'AR', label: 'Argentina' },
	{ value: 'AM', label: 'Armenia' },
	{ value: 'AW', label: 'Aruba' },
	{ value: 'AT', label: 'Austria' },
	{ value: 'AZ', label: 'Azerbaijan' },
	{ value: 'BS', label: 'Bahamas' },
	{ value: 'BH', label: 'Bahrain' },
	{ value: 'BD', label: 'Bangladesh' },
	{ value: 'BB', label: 'Barbados' },
	{ value: 'BY', label: 'Belarus' },
	{ value: 'BE', label: 'Belgium' },
	{ value: 'BZ', label: 'Belize' },
	{ value: 'BJ', label: 'Benin' },
	{ value: 'BM', label: 'Bermuda' },
	{ value: 'BT', label: 'Bhutan' },
	{ value: 'BO', label: 'Bolivia' },
	{ value: 'BQ', label: 'Bonaire, Sint Eustatius and Saba' },
	{ value: 'BA', label: 'Bosnia and Herzegovina' },
	{ value: 'BW', label: 'Botswana' },
	{ value: 'BV', label: 'Bouvet Island' },
	{ value: 'BR', label: 'Brazil' },
	{ value: 'IO', label: 'British Indian Ocean Territory' },
	{ value: 'BN', label: 'Brunei Darussalam' },
	{ value: 'BG', label: 'Bulgaria' },
	{ value: 'BF', label: 'Burkina Faso' },
	{ value: 'BI', label: 'Burundi' },
	{ value: 'CV', label: 'Cabo Verde' },
	{ value: 'KH', label: 'Cambodia' },
	{ value: 'CM', label: 'Cameroon' },
	{ value: 'KY', label: 'Cayman Islands' },
	{ value: 'CF', label: 'Central African Republic' },
	{ value: 'TD', label: 'Chad' },
	{ value: 'CL', label: 'Chile' },
	{ value: 'CN', label: 'China' },
	{ value: 'CX', label: 'Christmas Island' },
	{ value: 'CC', label: 'Cocos (Keeling) Islands' },
	{ value: 'CO', label: 'Colombia' },
	{ value: 'KM', label: 'Comoros' },
	{ value: 'CD', label: 'Congo (the Democratic Republic of the)' },
	{ value: 'CG', label: 'Congo' },
	{ value: 'CK', label: 'Cook Islands' },
	{ value: 'CR', label: 'Costa Rica' },
	{ value: 'HR', label: 'Croatia' },
	{ value: 'CU', label: 'Cuba' },
	{ value: 'CW', label: 'Curaçao' },
	{ value: 'CY', label: 'Cyprus' },
	{ value: 'CZ', label: 'Czechia' },
	{ value: 'CI', label: "Côte d'Ivoire" },
	{ value: 'DK', label: 'Denmark' },
	{ value: 'DJ', label: 'Djibouti' },
	{ value: 'DM', label: 'Dominica' },
	{ value: 'DO', label: 'Dominican Republic' },
	{ value: 'EC', label: 'Ecuador' },
	{ value: 'EG', label: 'Egypt' },
	{ value: 'SV', label: 'El Salvador' },
	{ value: 'GQ', label: 'Equatorial Guinea' },
	{ value: 'ER', label: 'Eritrea' },
	{ value: 'EE', label: 'Estonia' },
	{ value: 'SZ', label: 'Eswatini' },
	{ value: 'ET', label: 'Ethiopia' },
	{ value: 'FK', label: 'Falkland Islands' },
	{ value: 'FO', label: 'Faroe Islands' },
	{ value: 'FJ', label: 'Fiji' },
	{ value: 'FI', label: 'Finland' },
	{ value: 'FR', label: 'France' },
	{ value: 'GF', label: 'French Guiana' },
	{ value: 'PF', label: 'French Polynesia' },
	{ value: 'TF', label: 'French Southern Territories' },
	{ value: 'GA', label: 'Gabon' },
	{ value: 'GM', label: 'Gambia' },
	{ value: 'GE', label: 'Georgia' },
	{ value: 'DE', label: 'Germany' },
	{ value: 'GH', label: 'Ghana' },
	{ value: 'GI', label: 'Gibraltar' },
	{ value: 'GR', label: 'Greece' },
	{ value: 'GL', label: 'Greenland' },
	{ value: 'GD', label: 'Grenada' },
	{ value: 'GP', label: 'Guadeloupe' },
	{ value: 'GU', label: 'Guam' },
	{ value: 'GT', label: 'Guatemala' },
	{ value: 'GG', label: 'Guernsey' },
	{ value: 'GN', label: 'Guinea' },
	{ value: 'GW', label: 'Guinea-Bissau' },
	{ value: 'GY', label: 'Guyana' },
	{ value: 'HT', label: 'Haiti' },
	{ value: 'HM', label: 'Heard Island and McDonald Islands' },
	{ value: 'VA', label: 'Holy See' },
	{ value: 'HN', label: 'Honduras' },
	{ value: 'HK', label: 'Hong Kong' },
	{ value: 'HU', label: 'Hungary' },
	{ value: 'IS', label: 'Iceland' },
	{ value: 'IN', label: 'India' },
	{ value: 'ID', label: 'Indonesia' },
	{ value: 'IR', label: 'Iran' },
	{ value: 'IQ', label: 'Iraq' },
	{ value: 'IE', label: 'Ireland' },
	{ value: 'IM', label: 'Isle of Man' },
	{ value: 'IL', label: 'Israel' },
	{ value: 'IT', label: 'Italy' },
	{ value: 'JM', label: 'Jamaica' },
	{ value: 'JP', label: 'Japan' },
	{ value: 'JE', label: 'Jersey' },
	{ value: 'JO', label: 'Jordan' },
	{ value: 'KZ', label: 'Kazakhstan' },
	{ value: 'KE', label: 'Kenya' },
	{ value: 'KI', label: 'Kiribati' },
	{ value: 'KP', label: "The Democratic People's Republic of Korea" },
	{ value: 'KR', label: 'Republic of Korea' },
	{ value: 'KW', label: 'Kuwait' },
	{ value: 'KG', label: 'Kyrgyzstan' },
	{ value: 'LA', label: "Lao People's Democratic Republic" },
	{ value: 'LV', label: 'Latvia' },
	{ value: 'LB', label: 'Lebanon' },
	{ value: 'LS', label: 'Lesotho' },
	{ value: 'LR', label: 'Liberia' },
	{ value: 'LY', label: 'Libya' },
	{ value: 'LI', label: 'Liechtenstein' },
	{ value: 'LT', label: 'Lithuania' },
	{ value: 'LU', label: 'Luxembourg' },
	{ value: 'MO', label: 'Macao' },
	{ value: 'MG', label: 'Madagascar' },
	{ value: 'MW', label: 'Malawi' },
	{ value: 'MY', label: 'Malaysia' },
	{ value: 'MV', label: 'Maldives' },
	{ value: 'ML', label: 'Mali' },
	{ value: 'MT', label: 'Malta' },
	{ value: 'MH', label: 'Marshall Islands' },
	{ value: 'MQ', label: 'Martinique' },
	{ value: 'MR', label: 'Mauritania' },
	{ value: 'MU', label: 'Mauritius' },
	{ value: 'YT', label: 'Mayotte' },
	{ value: 'MX', label: 'Mexico' },
	{ value: 'FM', label: 'Micronesia' },
	{ value: 'MD', label: 'Moldova' },
	{ value: 'MC', label: 'Monaco' },
	{ value: 'MN', label: 'Mongolia' },
	{ value: 'ME', label: 'Montenegro' },
	{ value: 'MS', label: 'Montserrat' },
	{ value: 'MA', label: 'Morocco' },
	{ value: 'MZ', label: 'Mozambique' },
	{ value: 'MM', label: 'Myanmar' },
	{ value: 'NA', label: 'Namibia' },
	{ value: 'NR', label: 'Nauru' },
	{ value: 'NP', label: 'Nepal' },
	{ value: 'NL', label: 'Netherlands' },
	{ value: 'NC', label: 'New Caledonia' },
	{ value: 'NI', label: 'Nicaragua' },
	{ value: 'NE', label: 'Niger' },
	{ value: 'NG', label: 'Nigeria' },
	{ value: 'NU', label: 'Niue' },
	{ value: 'NF', label: 'Norfolk Island' },
	{ value: 'MP', label: 'Northern Mariana Islands' },
	{ value: 'NO', label: 'Norway' },
	{ value: 'OM', label: 'Oman' },
	{ value: 'PK', label: 'Pakistan' },
	{ value: 'PW', label: 'Palau' },
	{ value: 'PS', label: 'Palestine, State of' },
	{ value: 'PA', label: 'Panama' },
	{ value: 'PG', label: 'Papua New Guinea' },
	{ value: 'PY', label: 'Paraguay' },
	{ value: 'PE', label: 'Peru' },
	{ value: 'PH', label: 'Philippines' },
	{ value: 'PN', label: 'Pitcairn' },
	{ value: 'PL', label: 'Poland' },
	{ value: 'PT', label: 'Portugal' },
	{ value: 'PR', label: 'Puerto Rico' },
	{ value: 'QA', label: 'Qatar' },
	{ value: 'MK', label: 'Republic of North Macedonia' },
	{ value: 'RO', label: 'Romania' },
	{ value: 'RU', label: 'Russian Federation' },
	{ value: 'RW', label: 'Rwanda' },
	{ value: 'RE', label: 'Réunion' },
	{ value: 'BL', label: 'Saint Barthélemy' },
	{ value: 'SH', label: 'Saint Helena, Ascension and Tristan da Cunha' },
	{ value: 'KN', label: 'Saint Kitts and Nevis' },
	{ value: 'LC', label: 'Saint Lucia' },
	{ value: 'MF', label: 'Saint Martin' },
	{ value: 'PM', label: 'Saint Pierre and Miquelon' },
	{ value: 'VC', label: 'Saint Vincent and the Grenadines' },
	{ value: 'WS', label: 'Samoa' },
	{ value: 'SM', label: 'San Marino' },
	{ value: 'ST', label: 'Sao Tome and Principe' },
	{ value: 'SA', label: 'Saudi Arabia' },
	{ value: 'SN', label: 'Senegal' },
	{ value: 'RS', label: 'Serbia' },
	{ value: 'SC', label: 'Seychelles' },
	{ value: 'SL', label: 'Sierra Leone' },
	{ value: 'SG', label: 'Singapore' },
	{ value: 'SX', label: 'Sint Maarten' },
	{ value: 'SK', label: 'Slovakia' },
	{ value: 'SI', label: 'Slovenia' },
	{ value: 'SB', label: 'Solomon Islands' },
	{ value: 'SO', label: 'Somalia' },
	{ value: 'ZA', label: 'South Africa' },
	{ value: 'GS', label: 'South Georgia and the South Sandwich Islands' },
	{ value: 'SS', label: 'South Sudan' },
	{ value: 'ES', label: 'Spain' },
	{ value: 'LK', label: 'Sri Lanka' },
	{ value: 'SD', label: 'Sudan' },
	{ value: 'SR', label: 'Surinamer' },
	{ value: 'SJ', label: 'Svalbard and Jan Mayen' },
	{ value: 'SE', label: 'Sweden' },
	{ value: 'CH', label: 'Switzerland' },
	{ value: 'SY', label: 'Syrian Arab Republic' },
	{ value: 'TW', label: 'Taiwan' },
	{ value: 'TJ', label: 'Tajikistan' },
	{ value: 'TZ', label: 'Tanzania, United Republic of' },
	{ value: 'TH', label: 'Thailand' },
	{ value: 'TL', label: 'Timor-Leste' },
	{ value: 'TG', label: 'Togo' },
	{ value: 'TK', label: 'Tokelau' },
	{ value: 'TO', label: 'Tonga' },
	{ value: 'TT', label: 'Trinidad and Tobago' },
	{ value: 'TN', label: 'Tunisia' },
	{ value: 'TR', label: 'Turkey' },
	{ value: 'TM', label: 'Turkmenistan' },
	{ value: 'TC', label: 'Turks and Caicos Islands' },
	{ value: 'TV', label: 'Tuvalu' },
	{ value: 'UG', label: 'Uganda' },
	{ value: 'UA', label: 'Ukraine' },
	{ value: 'AE', label: 'United Arab Emirates' },
	{ value: 'UM', label: 'United States Minor Outlying Islands' },
	{ value: 'UY', label: 'Uruguay' },
	{ value: 'UZ', label: 'Uzbekistan' },
	{ value: 'VU', label: 'Vanuatu' },
	{ value: 'VE', label: 'Venezuela (Bolivarian Republic of)' },
	{ value: 'VN', label: 'Viet Nam' },
	{ value: 'VG', label: 'Virgin Islands (British)' },
	{ value: 'VI', label: 'Virgin Islands (U.S.)' },
	{ value: 'WF', label: 'Wallis and Futuna' },
	{ value: 'EH', label: 'Western Sahara' },
	{ value: 'YE', label: 'Yemen' },
	{ value: 'ZM', label: 'Zambia' },
	{ value: 'ZW', label: 'Zimbabwe' },
	{ value: 'AX', label: 'Åland Islands' },
];
export const countryLabels = {
	GB: 'United Kingdom',
	AU: 'Australia',
	CA: 'Canada',
	NZ: 'New Zealand',
	US: 'United States of America',
	AF: 'Afghanistan',
	AL: 'Albania',
	DZ: 'Algeria',
	AS: 'American Samoa',
	AD: 'Andorra',
	AO: 'Angola',
	AI: 'Anguilla',
	AQ: 'Antarctica',
	AG: 'Antigua and Barbuda',
	AR: 'Argentina',
	AM: 'Armenia',
	AW: 'Aruba',
	AT: 'Austria',
	AZ: 'Azerbaijan',
	BS: 'Bahamas',
	BH: 'Bahrain',
	BD: 'Bangladesh',
	BB: 'Barbados',
	BY: 'Belarus',
	BE: 'Belgium',
	BZ: 'Belize',
	BJ: 'Benin',
	BM: 'Bermuda',
	BT: 'Bhutan',
	BO: 'Bolivia',
	BQ: 'Bonaire, Sint Eustatius and Saba',
	BA: 'Bosnia and Herzegovina',
	BW: 'Botswana',
	BV: 'Bouvet Island',
	BR: 'Brazil',
	IO: 'British Indian Ocean Territory',
	BN: 'Brunei Darussalam',
	BG: 'Bulgaria',
	BF: 'Burkina Faso',
	BI: 'Burundi',
	CV: 'Cabo Verde',
	KH: 'Cambodia',
	CM: 'Cameroon',
	KY: 'Cayman Islands',
	CF: 'Central African Republic',
	TD: 'Chad',
	CL: 'Chile',
	CN: 'China',
	CX: 'Christmas Island',
	CC: 'Cocos (Keeling) Islands',
	CO: 'Colombia',
	KM: 'Comoros',
	CD: 'Congo (the Democratic Republic of the)',
	CG: 'Congo',
	CK: 'Cook Islands',
	CR: 'Costa Rica',
	HR: 'Croatia',
	CU: 'Cuba',
	CW: 'Curaçao',
	CY: 'Cyprus',
	CZ: 'Czechia',
	CI: "Côte d'Ivoire",
	DK: 'Denmark',
	DJ: 'Djibouti',
	DM: 'Dominica',
	DO: 'Dominican Republic',
	EC: 'Ecuador',
	EG: 'Egypt',
	SV: 'El Salvador',
	GQ: 'Equatorial Guinea',
	ER: 'Eritrea',
	EE: 'Estonia',
	SZ: 'Eswatini',
	ET: 'Ethiopia',
	FK: 'Falkland Islands',
	FO: 'Faroe Islands',
	FJ: 'Fiji',
	FI: 'Finland',
	FR: 'France',
	GF: 'French Guiana',
	PF: 'French Polynesia',
	TF: 'French Southern Territories',
	GA: 'Gabon',
	GM: 'Gambia',
	GE: 'Georgia',
	DE: 'Germany',
	GH: 'Ghana',
	GI: 'Gibraltar',
	GR: 'Greece',
	GL: 'Greenland',
	GD: 'Grenada',
	GP: 'Guadeloupe',
	GU: 'Guam',
	GT: 'Guatemala',
	GG: 'Guernsey',
	GN: 'Guinea',
	GW: 'Guinea-Bissau',
	GY: 'Guyana',
	HT: 'Haiti',
	HM: 'Heard Island and McDonald Islands',
	VA: 'Holy See',
	HN: 'Honduras',
	HK: 'Hong Kong',
	HU: 'Hungary',
	IS: 'Iceland',
	IN: 'India',
	ID: 'Indonesia',
	IR: 'Iran',
	IQ: 'Iraq',
	IE: 'Ireland',
	IM: 'Isle of Man',
	IL: 'Israel',
	IT: 'Italy',
	JM: 'Jamaica',
	JP: 'Japan',
	JE: 'Jersey',
	JO: 'Jordan',
	KZ: 'Kazakhstan',
	KE: 'Kenya',
	KI: 'Kiribati',
	KP: "The Democratic People's Republic of Korea",
	KR: 'Republic of Korea',
	KW: 'Kuwait',
	KG: 'Kyrgyzstan',
	LA: "Lao People's Democratic Republic",
	LV: 'Latvia',
	LB: 'Lebanon',
	LS: 'Lesotho',
	LR: 'Liberia',
	LY: 'Libya',
	LI: 'Liechtenstein',
	LT: 'Lithuania',
	LU: 'Luxembourg',
	MO: 'Macao',
	MG: 'Madagascar',
	MW: 'Malawi',
	MY: 'Malaysia',
	MV: 'Maldives',
	ML: 'Mali',
	MT: 'Malta',
	MH: 'Marshall Islands',
	MQ: 'Martinique',
	MR: 'Mauritania',
	MU: 'Mauritius',
	YT: 'Mayotte',
	MX: 'Mexico',
	FM: 'Micronesia',
	MD: 'Moldova',
	MC: 'Monaco',
	MN: 'Mongolia',
	ME: 'Montenegro',
	MS: 'Montserrat',
	MA: 'Morocco',
	MZ: 'Mozambique',
	MM: 'Myanmar',
	NA: 'Namibia',
	NR: 'Nauru',
	NP: 'Nepal',
	NL: 'Netherlands',
	NC: 'New Caledonia',
	NI: 'Nicaragua',
	NE: 'Niger',
	NG: 'Nigeria',
	NU: 'Niue',
	NF: 'Norfolk Island',
	MP: 'Northern Mariana Islands',
	NO: 'Norway',
	OM: 'Oman',
	PK: 'Pakistan',
	PW: 'Palau',
	PS: 'Palestine, State of',
	PA: 'Panama',
	PG: 'Papua New Guinea',
	PY: 'Paraguay',
	PE: 'Peru',
	PH: 'Philippines',
	PN: 'Pitcairn',
	PL: 'Poland',
	PT: 'Portugal',
	PR: 'Puerto Rico',
	QA: 'Qatar',
	MK: 'Republic of North Macedonia',
	RO: 'Romania',
	RU: 'Russian Federation',
	RW: 'Rwanda',
	RE: 'Réunion',
	BL: 'Saint Barthélemy',
	SH: 'Saint Helena, Ascension and Tristan da Cunha',
	KN: 'Saint Kitts and Nevis',
	LC: 'Saint Lucia',
	MF: 'Saint Martin',
	PM: 'Saint Pierre and Miquelon',
	VC: 'Saint Vincent and the Grenadines',
	WS: 'Samoa',
	SM: 'San Marino',
	ST: 'Sao Tome and Principe',
	SA: 'Saudi Arabia',
	SN: 'Senegal',
	RS: 'Serbia',
	SC: 'Seychelles',
	SL: 'Sierra Leone',
	SG: 'Singapore',
	SX: 'Sint Maarten',
	SK: 'Slovakia',
	SI: 'Slovenia',
	SB: 'Solomon Islands',
	SO: 'Somalia',
	ZA: 'South Africa',
	GS: 'South Georgia and the South Sandwich Islands',
	SS: 'South Sudan',
	ES: 'Spain',
	LK: 'Sri Lanka',
	SD: 'Sudan',
	SR: 'Surinamer',
	SJ: 'Svalbard and Jan Mayen',
	SE: 'Sweden',
	CH: 'Switzerland',
	SY: 'Syrian Arab Republic',
	TW: 'Taiwan',
	TJ: 'Tajikistan',
	TZ: 'Tanzania, United Republic of',
	TH: 'Thailand',
	TL: 'Timor-Leste',
	TG: 'Togo',
	TK: 'Tokelau',
	TO: 'Tonga',
	TT: 'Trinidad and Tobago',
	TN: 'Tunisia',
	TR: 'Turkey',
	TM: 'Turkmenistan',
	TC: 'Turks and Caicos Islands',
	TV: 'Tuvalu',
	UG: 'Uganda',
	UA: 'Ukraine',
	AE: 'United Arab Emirates',
	UM: 'United States Minor Outlying Islands',
	UY: 'Uruguay',
	UZ: 'Uzbekistan',
	VU: 'Vanuatu',
	VE: 'Venezuela (Bolivarian Republic of)',
	VN: 'Viet Nam',
	VG: 'Virgin Islands (British)',
	VI: 'Virgin Islands (U.S.)',
	WF: 'Wallis and Futuna',
	EH: 'Western Sahara',
	YE: 'Yemen',
	ZM: 'Zambia',
	ZW: 'Zimbabwe',
	AX: 'Åland Islands',
};

export const availableIcons = {
	Tick: faCheck,
	Star: faStar,
	Money: faMoneyBill,
	Briefcase: faBriefcase,
	Cross: faTimes,
	People: faUsers,
	Cog: faCog,
	New: faPlus,
	Handshake: faHandshake,
	Phone: faPhone,
	Video: faVideo,
	Passport: faPassport,
	Contract: faFileContract,
	Signature: faFileSignature,
	Pause: faPause,
	Calendar: faCalendar,
	Envelope: faEnvelope,
	Pen: faPen,
};

export const nationalities = [
	'Afghan',
	'Albanian',
	'Algerian',
	'American',
	'Andorran',
	'Angolan',
	'Antiguans',
	'Argentinean',
	'Armenian',
	'Australian',
	'Austrian',
	'Azerbaijani',
	'Bahamian',
	'Bahraini',
	'Bangladeshi',
	'Barbadian',
	'Barbudans',
	'Batswana',
	'Belarusian',
	'Belgian',
	'Belizean',
	'Beninese',
	'Bhutanese',
	'Bolivian',
	'Bosnian',
	'Brazilian',
	'British',
	'Bruneian',
	'Bulgarian',
	'Burkinabe',
	'Burmese',
	'Burundian',
	'Cambodian',
	'Cameroonian',
	'Canadian',
	'Cape Verdean',
	'Central African',
	'Chadian',
	'Chilean',
	'Chinese',
	'Colombian',
	'Comoran',
	'Congolese',
	'Costa Rican',
	'Croatian',
	'Cuban',
	'Cypriot',
	'Czech',
	'Danish',
	'Djibouti',
	'Dominican',
	'Dutch',
	'East Timorese',
	'Ecuadorean',
	'Egyptian',
	'Emirian',
	'Equatorial Guinean',
	'Eritrean',
	'Estonian',
	'Ethiopian',
	'Fijian',
	'Filipino',
	'Finnish',
	'French',
	'Gabonese',
	'Gambian',
	'Georgian',
	'German',
	'Ghanaian',
	'Greek',
	'Grenadian',
	'Guatemalan',
	'Guinea-Bissauan',
	'Guinean',
	'Guyanese',
	'Haitian',
	'Herzegovinian',
	'Honduran',
	'Hungarian',
	'I-Kiribati',
	'Icelander',
	'Indian',
	'Indonesian',
	'Iranian',
	'Iraqi',
	'Irish',
	'Israeli',
	'Italian',
	'Ivorian',
	'Jamaican',
	'Japanese',
	'Jordanian',
	'Kazakhstani',
	'Kenyan',
	'Kittian and Nevisian',
	'Kuwaiti',
	'Kyrgyz',
	'Laotian',
	'Latvian',
	'Lebanese',
	'Liberian',
	'Libyan',
	'Liechtensteiner',
	'Lithuanian',
	'Luxembourger',
	'Macedonian',
	'Malagasy',
	'Malawian',
	'Malaysian',
	'Maldivan',
	'Malian',
	'Maltese',
	'Marshallese',
	'Mauritanian',
	'Mauritian',
	'Mexican',
	'Micronesian',
	'Moldovan',
	'Monacan',
	'Mongolian',
	'Moroccan',
	'Mosotho',
	'Motswana',
	'Mozambican',
	'Namibian',
	'Nauruan',
	'Nepalese',
	'New Zealander',
	'Nicaraguan',
	'Nigerian',
	'Nigerien',
	'North Korean',
	'Northern Irish',
	'Norwegian',
	'Omani',
	'Pakistani',
	'Palauan',
	'Panamanian',
	'Papua New Guinean',
	'Paraguayan',
	'Peruvian',
	'Polish',
	'Portuguese',
	'Qatari',
	'Romanian',
	'Russian',
	'Rwandan',
	'Saint Lucian',
	'Salvadoran',
	'Samoan',
	'San Marinese',
	'Sao Tomean',
	'Saudi',
	'Scottish',
	'Senegalese',
	'Serbian',
	'Seychellois',
	'Sierra Leonean',
	'Singaporean',
	'Slovakian',
	'Slovenian',
	'Solomon Islander',
	'Somali',
	'South African',
	'South Korean',
	'Spanish',
	'Sri Lankan',
	'Sudanese',
	'Surinamer',
	'Swazi',
	'Swedish',
	'Swiss',
	'Syrian',
	'Taiwanese',
	'Tajik',
	'Tanzanian',
	'Thai',
	'Togolese',
	'Tongan',
	'Trinidadian or Tobagonian',
	'Tunisian',
	'Turkish',
	'Tuvaluan',
	'Ugandan',
	'Ukrainian',
	'Uruguayan',
	'Uzbekistani',
	'Venezuelan',
	'Vietnamese',
	'Welsh',
	'Yemenite',
	'Zambian',
	'Zimbabwean',
];

export const whiteEthnicityOptions = [
	{
		value: 'English, Welsh, Scottish, Northern Irish or British',
		label: 'English, Welsh, Scottish, Northern Irish or British',
	},
	{
		value: 'Irish',
		label: 'Irish',
	},
	{
		value: 'Gypsy or Irish Traveller',
		label: 'Gypsy or Irish Traveller',
	},
	{
		value: 'Any other White background',
		label: 'Any other White background',
	},
];

export const mixedEthnicityOptions = [
	{
		value: 'White and Black Caribbean',
		label: 'White and Black Caribbean',
	},
	{
		value: 'White and Black African',
		label: 'White and Black African',
	},
	{
		value: 'White and Asian',
		label: 'White and Asian',
	},
	{
		value: 'Any other Mixed or Multiple ethnic background',
		label: 'Any other Mixed or Multiple ethnic background',
	},
];

export const asianEthnicityOptions = [
	{
		value: 'Indian',
		label: 'Indian',
	},
	{
		value: 'Pakistani',
		label: 'Pakistani',
	},
	{
		value: 'Bangladeshi',
		label: 'Bangladeshi',
	},
	{
		value: 'Chinese',
		label: 'Chinese',
	},
	{
		value: 'Any other Asian background',
		label: 'Any other Asian background',
	},
];

export const blackEthnicityOptions = [
	{
		value: 'African',
		label: 'African',
	},
	{
		value: 'Caribbean',
		label: 'Caribbean',
	},
	{
		value: 'Any other Black, African or Caribbean background',
		label: 'Any other Black, African or Caribbean background',
	},
];

export const otherEthnicityOptions = [
	{
		value: 'Arab',
		label: 'Arab',
	},
	{
		value: 'Any other ethnic group',
		label: 'Any other ethnic group',
	},
];

export const groupedEthnicityOptions = [
	{
		label: 'White',
		options: whiteEthnicityOptions,
	},
	{
		label: 'Mixed or Multiple ethnic groups',
		options: mixedEthnicityOptions,
	},
	{
		label: 'Asian or Asian British',
		options: asianEthnicityOptions,
	},
	{
		label: 'Black, African, Caribbean or Black British',
		options: blackEthnicityOptions,
	},
	{
		label: 'Other ethnic group',
		options: otherEthnicityOptions,
	},
];

export const genders = [
	'Male',
	'Female',
	'Non-binary',
	'Transgender',
	'Intersex',
	'Prefer not to say',
];

export const NoteTypes = (type: 'Job' | 'Candidate' | 'Company') => {
	const types = [
		{
			label: 'General comments',
			value: 'GENERAL',
		},

		{
			label: 'Key information',
			value: 'KEY',
		},
		{
			label: 'Tasks',
			value: 'TASKS',
		},
		{
			label: 'Requisition approved',
			value: 'REQUISITION_APPROVED',
		},
		{
			label: 'Requisition declined',
			value: 'REQUISITION_DECLINED',
		},
	];

	if (type === 'Candidate') {
		types.push(
			{
				label: 'Recruiter comments',
				value: 'RECRUITER',
			},
			{
				label: 'Screening notes',
				value: 'SCREENING',
			},
			{
				label: 'Phone interview notes',
				value: 'PHONE_INTERVIEW',
			},
			{
				label: 'Face to face interview notes',
				value: 'FACE_INTERVIEW',
			},
		);
	}

	return types;
};

export const integrationCategories = [
	{ label: 'Job Posting', value: 'JOB_POSTING' },
	{ label: 'Video', value: 'VIDEO' },
	{ label: 'HR', value: 'HR' },
	{ label: 'Calendar', value: 'CALENDAR' },
	{ label: 'Messaging', value: 'MESSAGING' },
	{ label: 'Candidate Sourcing', value: 'CANDIDATE_SOURCING' },
];

export const integrationStates = [
	{ label: 'Active', value: 'ACTIVE' },
	{ label: 'Inactive', value: 'INACTIVE' },
	{ label: 'Requires Action', value: 'REQUIRES_ACTION' },
	{ label: 'Awaiting Approval', value: 'AWAITING_APPROVAL' },
];

export const initialIntegrationFilters = {
	filters: {
		category: [],
	},
	term: '',
};

export const defaultInterviewManualFilters: InterviewFilters = {
	filters: {
		showPastEvents: true,
	},
	term: '',
};

export const defaultNotificationManualFilters: NotificationFilters = {
	filters: {
		resourceType: null,
		unread: null,
	},
};

export const days = [
	{
		label: 'Monday',
		value: 1,
	},
	{
		label: 'Tuesday',
		value: 2,
	},
	{
		label: 'Wednesday',
		value: 3,
	},
	{
		label: 'Thursday',
		value: 4,
	},
	{
		label: 'Friday',
		value: 5,
	},
	{
		label: 'Saturday',
		value: 6,
	},
	{
		label: 'Sunday',
		value: 0,
	},
];

export const dates = [
	{ label: '1st', value: 1 },
	{ label: '2nd', value: 2 },
	{ label: '3rd', value: 3 },
	{ label: '4th', value: 4 },
	{ label: '5th', value: 5 },
	{ label: '6th', value: 6 },
	{ label: '7th', value: 7 },
	{ label: '8th', value: 8 },
	{ label: '9th', value: 9 },
	{ label: '10th', value: 10 },
	{ label: '11th', value: 11 },
	{ label: '12th', value: 12 },
	{ label: '13th', value: 13 },
	{ label: '14th', value: 14 },
	{ label: '15th', value: 15 },
	{ label: '17th', value: 17 },
	{ label: '18th', value: 18 },
	{ label: '19th', value: 19 },
	{ label: '20th', value: 20 },
	{ label: '21st', value: 21 },
	{ label: '22nd', value: 22 },
	{ label: '23rd', value: 23 },
	{ label: '24th', value: 24 },
	{ label: '25th', value: 25 },
	{ label: '26th', value: 26 },
	{ label: '27th', value: 27 },
	{ label: '28th', value: 28 },
	{ label: 'Last day of the month', value: 'LAST_DAY_OF_THE_MONTH' },
];

export const twentyFourHourTimes = [
	{ label: '00:00', value: 0 },
	{ label: '01:00', value: 1 },
	{ label: '02:00', value: 2 },
	{ label: '03:00', value: 3 },
	{ label: '04:00', value: 4 },
	{ label: '05:00', value: 5 },
	{ label: '06:00', value: 6 },
	{ label: '07:00', value: 7 },
	{ label: '08:00', value: 8 },
	{ label: '09:00', value: 9 },
	{ label: '10:00', value: 10 },
	{ label: '11:00', value: 11 },
	{ label: '12:00', value: 12 },
	{ label: '13:00', value: 13 },
	{ label: '14:00', value: 14 },
	{ label: '15:00', value: 15 },
	{ label: '16:00', value: 16 },
	{ label: '17:00', value: 17 },
	{ label: '18:00', value: 18 },
	{ label: '19:00', value: 19 },
	{ label: '20:00', value: 20 },
	{ label: '21:00', value: 21 },
	{ label: '22:00', value: 22 },
	{ label: '23:00', value: 23 },
];

export const durationOptions = [
	{ label: '15 minutes', value: 15 },
	{ label: '30 minutes', value: 30 },
	{ label: '45 minutes', value: 45 },
	{ label: '1 hour', value: 60 },
	{ label: '1 hour 15 minutes', value: 75 },
	{ label: '1 hour 30 minutes', value: 90 },
	{ label: '1 hour 45 minutes', value: 105 },
	{ label: '2 hours', value: 120 },
	{ label: '2 hours 15 minutes', value: 135 },
	{ label: '2 hours 30 minutes', value: 150 },
	{ label: '2 hour 45 minutes', value: 165 },
	{ label: '3 hours', value: 180 },
];

export const jobFlowIntegrationActions: {
	[key in ConfigurableIntegrations]: string[];
} = {
	SLACK: ['SLACK_MESSAGE'],
	HIBOB: [],
	CALENDAR_SYNC: [],
	ZOOM: [],
	TEAMS: ['TEAMS_MESSAGE'],
	SOURCE_CONNECT: [],
	EMAIL: [],
};

export const userAppIntegrations = ['TEAMS'];

export const basicJobFlowActions = ['EMAIL', 'INTERVIEW'];
export const permissionBasedJobFlowActions = ['ESIGNATURE', 'SURVEY'];
export const productBasedJobFlowActions = ['ESIGNATURE', 'SURVEY'];

export const providers: { [key in providerKeys]: string } = {
	gmail: 'Gmail',
	eas: 'Microsoft',
	ews: 'Microsoft',
	graph: 'Microsoft',
	hotmail: 'Hotmail',
	outlook: 'Outlook.com',
	yahoo: 'Yahoo',
	icloud: 'iCloud',
	generic: 'IMAP',
	custom: 'Custom',
	nylas: 'Nylas',
	zoom: 'Zoom',
	teams: 'Teams',
};

export const candidateSourceMap = {
	UNKNOWN: 'Unknown',
	CONTROL: 'Manual upload',
	JOB_BOARD: 'Careers Page',
	SOURCE: 'Source',
	STRYVE_CONNECT: 'LinkedIn',
	REGISTER_INTEREST: 'Registered interest',
	IMPORT: 'Imported via CSV',
} as { [key in CandidateSource]: string };

export const currencies: { [key in string]: string } = {
	AED: 'United Arab Emirates Dirham',
	AFN: 'Afghan Afghani',
	ALL: 'Albanian Lek',
	AMD: 'Armenian Dram',
	ANG: 'Netherlands Antillean Guilder',
	AOA: 'Angolan Kwanza',
	ARS: 'Argentine Peso',
	AUD: 'Australian Dollar',
	AWG: 'Aruban Florin',
	AZN: 'Azerbaijani Manat',
	BAM: 'Bosnia-Herzegovina Convertible Mark',
	BBD: 'Barbadian Dollar',
	BDT: 'Bangladeshi Taka',
	BGN: 'Bulgarian Lev',
	BHD: 'Bahraini Dinar',
	BIF: 'Burundian Franc',
	BMD: 'Bermudan Dollar',
	BND: 'Brunei Dollar',
	BOB: 'Bolivian Boliviano',
	BRL: 'Brazilian Real',
	BSD: 'Bahamian Dollar',
	BTC: 'Bitcoin',
	BTN: 'Bhutanese Ngultrum',
	BWP: 'Botswanan Pula',
	BYN: 'Belarusian Ruble',
	BZD: 'Belize Dollar',
	CAD: 'Canadian Dollar',
	CDF: 'Congolese Franc',
	CHF: 'Swiss Franc',
	CLF: 'Chilean Unit of Account (UF)',
	CLP: 'Chilean Peso',
	CNH: 'Chinese Yuan (Offshore)',
	CNY: 'Chinese Yuan',
	COP: 'Colombian Peso',
	CRC: 'Costa Rican Colón',
	CUC: 'Cuban Convertible Peso',
	CUP: 'Cuban Peso',
	CVE: 'Cape Verdean Escudo',
	CZK: 'Czech Republic Koruna',
	DJF: 'Djiboutian Franc',
	DKK: 'Danish Krone',
	DOP: 'Dominican Peso',
	DZD: 'Algerian Dinar',
	EGP: 'Egyptian Pound',
	ERN: 'Eritrean Nakfa',
	ETB: 'Ethiopian Birr',
	EUR: 'Euro',
	FJD: 'Fijian Dollar',
	FKP: 'Falkland Islands Pound',
	GBP: 'British Pound Sterling',
	GEL: 'Georgian Lari',
	GGP: 'Guernsey Pound',
	GHS: 'Ghanaian Cedi',
	GIP: 'Gibraltar Pound',
	GMD: 'Gambian Dalasi',
	GNF: 'Guinean Franc',
	GTQ: 'Guatemalan Quetzal',
	GYD: 'Guyanaese Dollar',
	HKD: 'Hong Kong Dollar',
	HNL: 'Honduran Lempira',
	HRK: 'Croatian Kuna',
	HTG: 'Haitian Gourde',
	HUF: 'Hungarian Forint',
	IDR: 'Indonesian Rupiah',
	ILS: 'Israeli New Sheqel',
	IMP: 'Manx pound',
	INR: 'Indian Rupee',
	IQD: 'Iraqi Dinar',
	IRR: 'Iranian Rial',
	ISK: 'Icelandic Króna',
	JEP: 'Jersey Pound',
	JMD: 'Jamaican Dollar',
	JOD: 'Jordanian Dinar',
	JPY: 'Japanese Yen',
	KES: 'Kenyan Shilling',
	KGS: 'Kyrgystani Som',
	KHR: 'Cambodian Riel',
	KMF: 'Comorian Franc',
	KPW: 'North Korean Won',
	KRW: 'South Korean Won',
	KWD: 'Kuwaiti Dinar',
	KYD: 'Cayman Islands Dollar',
	KZT: 'Kazakhstani Tenge',
	LAK: 'Laotian Kip',
	LBP: 'Lebanese Pound',
	LKR: 'Sri Lankan Rupee',
	LRD: 'Liberian Dollar',
	LSL: 'Lesotho Loti',
	LYD: 'Libyan Dinar',
	MAD: 'Moroccan Dirham',
	MDL: 'Moldovan Leu',
	MGA: 'Malagasy Ariary',
	MKD: 'Macedonian Denar',
	MMK: 'Myanma Kyat',
	MNT: 'Mongolian Tugrik',
	MOP: 'Macanese Pataca',
	MRU: 'Mauritanian Ouguiya',
	MUR: 'Mauritian Rupee',
	MVR: 'Maldivian Rufiyaa',
	MWK: 'Malawian Kwacha',
	MXN: 'Mexican Peso',
	MYR: 'Malaysian Ringgit',
	MZN: 'Mozambican Metical',
	NAD: 'Namibian Dollar',
	NGN: 'Nigerian Naira',
	NIO: 'Nicaraguan Córdoba',
	NOK: 'Norwegian Krone',
	NPR: 'Nepalese Rupee',
	NZD: 'New Zealand Dollar',
	OMR: 'Omani Rial',
	PAB: 'Panamanian Balboa',
	PEN: 'Peruvian Nuevo Sol',
	PGK: 'Papua New Guinean Kina',
	PHP: 'Philippine Peso',
	PKR: 'Pakistani Rupee',
	PLN: 'Polish Zloty',
	PYG: 'Paraguayan Guarani',
	QAR: 'Qatari Rial',
	RON: 'Romanian Leu',
	RSD: 'Serbian Dinar',
	RUB: 'Russian Ruble',
	RWF: 'Rwandan Franc',
	SAR: 'Saudi Riyal',
	SBD: 'Solomon Islands Dollar',
	SCR: 'Seychellois Rupee',
	SDG: 'Sudanese Pound',
	SEK: 'Swedish Krona',
	SGD: 'Singapore Dollar',
	SHP: 'Saint Helena Pound',
	SLL: 'Sierra Leonean Leone',
	SOS: 'Somali Shilling',
	SRD: 'Surinamese Dollar',
	SSP: 'South Sudanese Pound',
	STD: 'São Tomé and Príncipe Dobra (pre-2018)',
	STN: 'São Tomé and Príncipe Dobra',
	SVC: 'Salvadoran Colón',
	SYP: 'Syrian Pound',
	SZL: 'Swazi Lilangeni',
	THB: 'Thai Baht',
	TJS: 'Tajikistani Somoni',
	TMT: 'Turkmenistani Manat',
	TND: 'Tunisian Dinar',
	TOP: "Tongan Pa'anga",
	TRY: 'Turkish Lira',
	TTD: 'Trinidad and Tobago Dollar',
	TWD: 'New Taiwan Dollar',
	TZS: 'Tanzanian Shilling',
	UAH: 'Ukrainian Hryvnia',
	UGX: 'Ugandan Shilling',
	USD: 'United States Dollar',
	UYU: 'Uruguayan Peso',
	UZS: 'Uzbekistan Som',
	VEF: 'Venezuelan Bolívar Fuerte (Old)',
	VES: 'Venezuelan Bolívar Soberano',
	VND: 'Vietnamese Dong',
	VUV: 'Vanuatu Vatu',
	WST: 'Samoan Tala',
	XAF: 'CFA Franc BEAC',
	XAG: 'Silver Ounce',
	XAU: 'Gold Ounce',
	XCD: 'East Caribbean Dollar',
	XDR: 'Special Drawing Rights',
	XOF: 'CFA Franc BCEAO',
	XPD: 'Palladium Ounce',
	XPF: 'CFP Franc',
	XPT: 'Platinum Ounce',
	YER: 'Yemeni Rial',
	ZAR: 'South African Rand',
	ZMW: 'Zambian Kwacha',
	ZWL: 'Zimbabwean Dollar',
};

export const currencyOptions = Object.keys(currencies).map(key => {
	const currencyKey = currencies[key];
	return { label: `${key} - ${currencyKey}`, value: key };
});

export const industries = [
	'Accountancy',
	'Accountancy (Qualified)',
	'Admin & Secretarial',
	'Advertising & PR',
	'Aerospace',
	'Agriculture Fishing & Forestry',
	'Arts',
	'Automotive',
	'Banking',
	'Building & Construction',
	'Call Centre & Customer Service',
	'Charity & Voluntary',
	'Consultancy',
	'Defence & Military',
	'Design & Creative',
	'Education & Training',
	'Electronics',
	'Energy & Renewables',
	'Engineering',
	'Executive & Management',
	'Fashion',
	'Financial Services',
	'FMCG',
	'Graduate Roles',
	'Health & Safety',
	'Hospitality & Catering',
	'HR & Personnel',
	'Insurance',
	'Internet & New Media',
	'IT',
	'Legal',
	'Leisure & Sport',
	'Logistics Distribution & Supply Chain',
	'Manufacturing & Production',
	'Marketing',
	'Media',
	'Medical & Nursing',
	'Other',
	'Pharmaceuticals',
	'Property & Housing',
	'Public Sector & Government',
	'Purchasing & Procurement',
	'Recruitment Consultancy',
	'Retail',
	'Sales',
	'Science & Research',
	'Social Care',
	'Telecommunications',
	'Transport & Rail',
	'Travel & Tourism',
	'Utilities',
];

export const placeholderAPIResponse = {
	data: [],
	meta: {
		totalRecords: 0,
		page: 1,
	},
};

export const interviewPackQuestionTypes = ['FREE_TEXT', 'MULTIPLE_CHOICE'];

export const surveyQuestionTypes = [
	'FREE_TEXT',
	'MULTIPLE_CHOICE',
	'MULTIPLE_SELECT',
	'RANGE_SLIDER',
	'BOOLEAN',
	'RATING',
	'DATA_COLLECTION',
];

export const surveyDataCollectionFields = [
	'GENDER',
	'NATIONALITY',
	'ETHNICITY',
];

export const basePlacement: Partial<CompanyPlacement> = {
	department: '',
	remunerationPeriod: 'YEAR',
	remunerationAmount: 0,
	bonusAmount: null,
	bonusPercentage: null,
	benefits: '',
	baseSalary: '',
	startDate: new Date(),
	contractType: null,
	placementType: '',
	workingPattern: null,
	currency: '',
	notes: '',
	hirers: [],
};

export const standardJobStatuses: Job['status'][] = [
	'LIVE',
	'DRAFT',
	'COMPLETED',
	'ARCHIVED',
];
export const requisitionJobStatuses: Job['status'][] = [
	'PENDING_SUBMISSION',
	'PENDING_APPROVAL',
];
export const allJobStatus: Job['status'][] = [
	...standardJobStatuses,
	...requisitionJobStatuses,
];

export const workingPreferences = [
	{ label: 'On site / office-based', value: 'NONE' },
	{ label: 'Hybrid', value: 'PARTIAL' },
	{ label: 'Fully remote', value: 'FULL' },
];

export const workingPreferenceLabels = {
	NONE: 'On site / office-based',
	PARTIAL: 'Hybrid',
	FULL: 'Fully remote',
};

const applicationFormQuestionTypes = {
	STRING: 'Text',
	NUMBER: 'Number',
	BOOLEAN: 'Toggle',
};

export const placementTypes = [
	{ label: 'Filled', value: 'FILLED' },
	{ label: 'Hold', value: 'HOLD' },
	{ label: 'Lost', value: 'LOST' },
	{ label: 'Other', value: 'OTHER' },
];

export const placementTypeLabels = placementTypes.reduce(
	(result, value) => ({ ...result, [value.value]: value.label }),
	{},
);

export const companyFileTypes = [
	{ label: 'General', value: 'GENERAL' },
	{ label: 'Terms of Business', value: 'TERMS_OF_BUSINESS' },
	{
		label: 'Company Presentation',
		value: 'COMPANY_PRESENTATION',
	},
	{ label: 'GDPR', value: 'GDPR' },
];

export const companyFileTypeLabels = companyFileTypes.reduce(
	(result, value) => ({ ...result, [value.value]: value.label }),
	{},
);

export const candidateFileTypes = [
	{ label: 'CV', value: 'CV' },
	{ label: 'Image', value: 'IMAGE' },
	{ label: 'Reference', value: 'REFERENCE' },
	{ label: 'Qualification', value: 'QUALIFICATION' },
	{ label: 'ID', value: 'ID' },
	{ label: 'Portfolio', value: 'PORTFOLIO' },
	{ label: 'Other', value: 'OTHER' },
];

export const candidateFileTypeLabels = candidateFileTypes.reduce(
	(result, value) => ({ ...result, [value.value]: value.label }),
	{},
);

export const jobStatuses = [
	{ label: 'Live', value: 'LIVE' },
	{ label: 'Archived', value: 'ARCHIVED' },
	{ label: 'Draft', value: 'DRAFT' },
	{ label: 'Pending Submission', value: 'PENDING_SUBMISSION' },
	{ label: 'Pending Review', value: 'PENDING_REVIEW' },
	{ label: 'Completed', value: 'COMPLETED' },
];

export const jobStatusLabels = jobStatuses.reduce(
	(result, value) => ({ ...result, [value.value]: value.label }),
	{},
);

export const stageTypeLabels = {
	OTHER: 'Other',
	INTERVIEW: 'Interview',
	INTERNAL_DISCUSSION: 'Internal Discussion',
	HOLD: 'Hold',
	OFFERED: 'Offered',
	ACCEPTED: 'Accepted',
	NEW: 'New',
	DECLINED: 'Declined',
	TEST: 'Test',
	SOURCE: 'Source',
};

export const mappedAuditValues = {
	remunerationPeriod: remunerationPeriodLabels,
	remoteWorkingAvailable: workingPreferenceLabels,
	workingPattern: workingPatterns,
	contractType: contractTypes,
	currency: currencies,
	locationCountry: countryLabels,
	country: countryLabels,
	remoteWorkingPreference: workingPreferenceLabels,
	jobTypePreference: { ...contractTypes, ...workingPatterns },
	level: educationLevelsLabels,
	questionType: applicationFormQuestionTypes,
	addressType: addressTypeLabels,
	placementType: placementTypeLabels,
	fileType: { ...companyFileTypeLabels, ...candidateFileTypeLabels },
	actionType: {
		TEAMS_MESSAGE: 'Microsoft Teams',
		SLACK_MESSAGE: 'Slack',
		EMAIL: 'Email',
		INTERVIEW: 'Interview',
		SURVEY: 'Survey',
		ESIGNATURE: 'Stryve Sign',
	},
	status: jobStatusLabels,
	bucketType: stageTypeLabels,
};

export const defaultAuditLogFilters: AuditLogFilterTypes = {
	filters: {
		createdAt: {
			lt: dayjs().endOf('day').toISOString(),
			gt: dayjs().subtract(1, 'year').startOf('day').toISOString(),
		},
		path: [],
		user: undefined,
	},
};

export const auditLogMappedLabels = {
	opensAt: 'Opening date',
	closesAt: 'Closing date',
	locationCountry: 'Country',
	locationCity: 'Town',
	remoteWorkingAvailable: 'Remote working',
	cv: 'CV',
	showOnJobSite: 'Show on Careers Page',
	VATNumber: 'VAT number',
	'flow.buckets': 'Job flow stages',
	'flow.buckets.actions': 'Job flow actions',
	'applicationForm.questions': 'Application form questions',
	'applicationForm.links': 'Application form links',
	'applicationForm.files': 'Application form files',
	entityCandidate: 'Candidate',
	line1: 'Address 1',
	line2: 'Address 2',
	location: 'File path',
	slack_message: 'Slack message',
	teams_message: 'Microsoft Teams message',
	bucketType: 'Stage type',
	URL: 'URL',
};

export const ignoredESignaturePlaceholders = ['{{signature}}', '{{date}}'];

export const eventStatus = {
	CONFIRMED: 'Confirmed',
	AWAITING_CANDIDATE_CONFIRMATION: 'Awaiting candidate date selection',
	CANDIDATE_DECLINED: 'Candidate declined',
	CANDIDATE_ADDITIONAL_REQUEST_TIMES: 'Candidate requested more times',
	CREATED: 'Pending confirmation',
};
